import { useState, useEffect, useRef } from "react";
import { Modal, TextInput, Textarea, Select, Switch, LoadingOverlay, Text, Group, FileButton, Box, SimpleGrid } from "@mantine/core";
import { Button, Alert, spacePx } from "@xplatform/design-system";
import { IconUpload, IconX } from "@tabler/icons-react";
import { useTranslation } from "@hooks/useTranslation";
import { useDoctype } from "@hooks/flow";
import { useCategory } from "@hooks/useCategory";
import { FormSection } from "../FormSection";
import { MappingTargetFields } from "../MappingTargetFields";
import "./CreateDoctypeDialog.scss";

interface CreateDoctypeDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

export const CreateDoctypeDialog = ({ open, onClose, onSuccess }: CreateDoctypeDialogProps) => {
    const { t } = useTranslation();
    const { createDoctype, isLoading } = useDoctype();
    const { categories, fetchCategories } = useCategory();
    const [doctypeName, setDoctypeName] = useState("");
    const [doctypeCode, setDoctypeCode] = useState("");
    const [categoryId, setCategoryId] = useState<string | null>(null);
    const [description, setDescription] = useState("");
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [mappingEnabled, setMappingEnabled] = useState(false);
    const [mappingService, setMappingService] = useState("");
    const [mappingPluginId, setMappingPluginId] = useState("");
    const [sharepointEnabled, setSharepointEnabled] = useState(false);
    const [spHostname, setSpHostname] = useState("");
    const [spSitePath, setSpSitePath] = useState("");
    const [spRootFolder, setSpRootFolder] = useState("");
    const [spTenantId, setSpTenantId] = useState("");
    const [spClientId, setSpClientId] = useState("");
    const [spClientSecret, setSpClientSecret] = useState("");
    const [error, setError] = useState<string | null>(null);
    const resetRef = useRef<() => void>(null);

    useEffect(() => {
        if (open) {
            fetchCategories();
        }
    }, [open, fetchCategories]);

    const handleClose = () => {
        setDoctypeName("");
        setDoctypeCode("");
        setCategoryId(null);
        setDescription("");
        setLogoFile(null);
        setLogoPreview(null);
        setMappingEnabled(false);
        setMappingService("");
        setMappingPluginId("");
        setSharepointEnabled(false);
        setSpHostname(""); setSpSitePath(""); setSpRootFolder("");
        setSpTenantId(""); setSpClientId(""); setSpClientSecret("");
        setError(null);
        resetRef.current?.();
        onClose();
    };

    const handleSubmit = async () => {
        setError(null);

        if (!doctypeName.trim()) {
            setError("Document type name is required");
            return;
        }

        if (!doctypeCode.trim()) {
            setError("Document type code is required");
            return;
        }

        if (!categoryId) {
            setError("Category is required");
            return;
        }

        if (mappingEnabled && (!mappingService.trim() || !mappingPluginId.trim())) {
            setError("Mapping service and plugin ID are required when mapping is enabled");
            return;
        }

        if (sharepointEnabled && [spHostname, spSitePath, spRootFolder, spTenantId, spClientId, spClientSecret].some((value) => !value.trim())) {
            setError("All SharePoint connection fields are required when enabled.");
            return;
        }

        try {
            const doctypeData = {
                docTypeName: doctypeName,
                docTypeCode: doctypeCode,
                categoryId,
                description: description || undefined,
                status: "active" as const,
                logo: logoFile || undefined,
                mappingEnabled,
                mappingService: mappingEnabled ? mappingService.trim() : null,
                mappingPluginId: mappingEnabled ? mappingPluginId.trim() : null,
                sharepointConfig: sharepointEnabled ? {
                    hostname: spHostname.trim(),
                    site_path: spSitePath.trim(),
                    root_folder: spRootFolder.trim(),
                    tenant_id: spTenantId.trim(),
                    client_id: spClientId.trim(),
                    client_secret: spClientSecret.trim(),
                } : undefined,
            };

            await createDoctype(doctypeData);

            if (onSuccess) {
                onSuccess();
            }

            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to create document type");
        }
    };

    const handleNameChange = (value: string) => {
        setDoctypeName(value);
        const code = value
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
        setDoctypeCode(code);
    };

    const handleLogoChange = (file: File | null) => {
        setLogoFile(file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            setLogoPreview(null);
        }
    };

    const categoryData = categories.map((cat) => ({
        value: cat.id,
        label: cat.name,
    }));

    return (
        <Modal
            opened={open}
            onClose={handleClose}
            title={t("admin.documentTypes.actions.createDocType")}
            size="90%"
            className="create-doctype-dialog"
            closeButtonProps={{ disabled: isLoading, "aria-label": "Close" }}
            portalProps={{ target: '#admin-portal-root' }}
        >
            <LoadingOverlay visible={isLoading} />

            {error && (
                <Alert color="red" className="create-doctype-dialog__error" mb="md" icon={<IconX size={16} />}>
                    {error}
                </Alert>
            )}

            <div className="create-doctype-dialog__form">
                <FormSection title={t("admin.documentTypes.sections.general")}>
                    <TextInput
                        label={t("admin.table.columns.name")}
                        value={doctypeName}
                        onChange={(e) => handleNameChange(e.currentTarget.value)}
                        required
                        autoFocus
                    />

                    <TextInput
                        label={t("admin.table.columns.code")}
                        value={doctypeCode}
                        onChange={(e) => setDoctypeCode(e.currentTarget.value)}
                        required
                        description="Unique identifier (auto-generated from name)"
                    />

                    <Select
                        label={t("admin.categories.title")}
                        value={categoryId}
                        onChange={setCategoryId}
                        data={categoryData}
                        required
                        searchable
                        comboboxProps={{ withinPortal: false }}
                    />

                    <Textarea
                        label={t("admin.table.columns.description")}
                        value={description}
                        onChange={(e) => setDescription(e.currentTarget.value)}
                        rows={3}
                    />

                    <Box>
                        <Text size="sm" fw={500} mb={spacePx[1]}>
                            {t("admin.documentTypes.labels.logo")} ({t("admin.documentTypes.labels.optional")})
                        </Text>
                        <FileButton
                            resetRef={resetRef}
                            onChange={handleLogoChange}
                            accept="image/*"
                        >
                            {(props) => (
                                <Button
                                    variant="outline"
                                    size="xs"
                                    leftSection={<IconUpload size={14} />}
                                    {...props}
                                >
                                    {logoFile ? logoFile.name : t("admin.documentTypes.actions.uploadLogo")}
                                </Button>
                            )}
                        </FileButton>
                        {logoPreview && (
                            <Box mt="xs">
                                <img
                                    src={logoPreview}
                                    alt={t("admin.documentTypes.labels.logoPreview")}
                                    style={{ maxWidth: 100, maxHeight: 100, objectFit: "contain" }}
                                />
                            </Box>
                        )}
                    </Box>
                </FormSection>

                <FormSection
                    title={t("admin.documentTypes.sections.mapping")}
                    description={t("admin.documentTypes.mapping.enableHint")}
                >
                    <Box>
                        <Switch
                            label={t("admin.documentTypes.mapping.enable")}
                            checked={mappingEnabled}
                            onChange={(e) => setMappingEnabled(e.currentTarget.checked)}
                        />
                        {mappingEnabled && (
                            <MappingTargetFields
                                service={mappingService}
                                pluginId={mappingPluginId}
                                onServiceChange={setMappingService}
                                onPluginIdChange={setMappingPluginId}
                            />
                        )}
                    </Box>
                </FormSection>

                <FormSection
                    title={t("admin.documentTypes.sections.sharepoint")}
                    description={t("admin.documentTypes.sharepoint.sectionHint")}
                >
                    <Box>
                        <Switch
                            label={t("admin.documentTypes.sharepoint.enable")}
                            description={t("admin.documentTypes.sharepoint.createHint")}
                            checked={sharepointEnabled}
                            onChange={(event) => setSharepointEnabled(event.currentTarget.checked)}
                        />
                        {sharepointEnabled && (
                            <SimpleGrid cols={{ base: 1, sm: 2 }} mt="sm">
                                <TextInput label={t("admin.documentTypes.sharepoint.hostname")} placeholder="example.sharepoint.com" value={spHostname} onChange={(event) => setSpHostname(event.currentTarget.value)} required />
                                <TextInput label={t("admin.documentTypes.sharepoint.sitePath")} placeholder="sites/Team" value={spSitePath} onChange={(event) => setSpSitePath(event.currentTarget.value)} required />
                                <TextInput label={t("admin.documentTypes.sharepoint.rootFolder")} placeholder="General/PDFs" value={spRootFolder} onChange={(event) => setSpRootFolder(event.currentTarget.value)} required />
                                <TextInput label={t("admin.documentTypes.sharepoint.tenantId")} value={spTenantId} onChange={(event) => setSpTenantId(event.currentTarget.value)} required />
                                <TextInput label={t("admin.documentTypes.sharepoint.clientId")} value={spClientId} onChange={(event) => setSpClientId(event.currentTarget.value)} required />
                                <TextInput label={t("admin.documentTypes.sharepoint.clientSecret")} type="password" autoComplete="new-password" value={spClientSecret} onChange={(event) => setSpClientSecret(event.currentTarget.value)} required />
                            </SimpleGrid>
                        )}
                    </Box>
                </FormSection>
            </div>

            <Group justify="flex-end" mt="lg" className="create-doctype-dialog__actions">
                <Button variant="default" onClick={handleClose} disabled={isLoading}>
                    {t("common.cancel")}
                </Button>
                <Button onClick={handleSubmit} disabled={isLoading}>
                    {t("common.save")}
                </Button>
            </Group>
        </Modal>
    );
};
